<template>
  <v-app
    class="d-flex align-center flex-column"
    :class="{ alternatTheme: $globals.alternatTheme }"
    v-if="readyToRender"
  >
    <v-main>
      <div class="circle" v-if="error.statusCode === 404">
        <h3 class="circle__number">404</h3>
        <h1 class="circle__head">{{ $t('notFound.head') }}</h1>
        <h2 class="circle__desc">
          {{ $t('notFound.desc') }}
        </h2>
      </div>
      <div class="circle" v-else>
        <h3 class="circle__number">{{ error.statusCode }}</h3>
        <h1 class="circle__head">{{ errorMessage }}</h1>
        <h2 class="circle__desc">{{ errorDescription }}</h2>
      </div>
      <div class="parental-text" v-if="$parentalControl.isActive">
        {{ $t('ParentalControl.errorPage') }}
      </div>
      <get-started
        customBtnText="error.btnText"
        :customBtnLink="$globals.links.homeUrl"
        :isNotFoundError="error.statusCode === 404"
      ></get-started>
    </v-main>
  </v-app>
</template>

<script lang="ts" setup>
const readyToRender = ref(false);

const { $globals, $t, $login, $customGtm, $logger, $api, $stage, $i18n } =
  useNuxtApp();
const i18nheaders = useLocaleHead({
  addDirAttribute: true,
  identifierAttribute: 'id',
  addSeoAttributes: true,
});
const scripts = $api.tenant?.default?.scripts;

const defaultScripts = scripts
  ?.filter((s: any) => s.default && (!s.env || s.env === ($stage || 'stg')))
  ?.map((s: any) => s.script);
const tenantLocales = $api.tenant?.default.locale?.supportedLocales;

useHead({
  title: $t('main.title'),
  script: defaultScripts,
  htmlAttrs: {
    ...i18nheaders.value.htmlAttrs,
  },
  link: [
    ...i18nheaders.value.link.filter(
      (i: any) =>
        i.rel === 'canonical' ||
        i.hreflang === 'x-default' ||
        tenantLocales?.includes(i.hreflang),
    ),
    {
      rel: 'icon',
      type: 'image/png',
      href: $api.placebo.placebo($globals.images['favicon'], 0, 0, 'png'),
    },
    {
      rel: 'manifest',
      href: '/manifest.json',
      crossorigin: 'use-credentials',
      id: 'dynamic-manifest',
    },
  ],
  meta: [
    ...i18nheaders.value.meta.filter(
      (i: any) =>
        i.property === 'og:url' ||
        i.property === 'og:locale' ||
        tenantLocales?.includes(i.content),
    ),
    {
      hid: 'description',
      name: 'description',
      content: $globals.metaData?.content
        ? `Enjoy the world of ${$globals.metaData?.content}`
        : $t('metaTag.desc'),
    },
  ],
});
const { error } = defineProps({
  error: {
    type: Object,
    statusCode: Number,
    default: null,
  },
});
if (import.meta.server) {
  $logger.error(
    `Error occured with status code ${error.statusCode} - ${error.message} - at ${error.url} - stack:${error.stack.toString()}`,
    {
      stack: error.stack,
    },
  );
}

// Construct translation keys dynamically based on the error status code
const errorMessage = $t(`${error.statusCode}.head`) || $t('defaultError.head');
const errorDescription =
  $t(`${error.statusCode}.desc`) || $t('defaultError.desc');
if ($globals.customStyle) {
  useHead({
    link: [
      {
        rel: 'stylesheet',
        type: 'text/css',
        href: $globals.customStyle,
      },
    ],
  });
}

onMounted(() => {
  $login.addLogoutListner();
  $customGtm.track({
    EN: 'View',
    PT: 'error page',
    PC: 'error page',
    adobeTrack: {
      adobeET: 'pageview',
      adobeData: {
        error_action: 'displayed',
        error_area: 'error page',
        error_code: error.statusCode,
        error_message: $t('notFound.head'),
        error_type: error.statusCode,
      },
    },
  });
});

watch(
  () => $globals.colors,
  (newColors) => {
    if (newColors) {
      useHead({
        style: [
          {
            id: 'theme-colors',
            key: 'theme',
            innerHTML: `body {
        ${newColors}
      };`,
          },
        ],
        link: [
          {
            rel: 'stylesheet',
            type: 'text/css',
            href: $globals.customStyle,
          },
        ],
      });
      if ($globals.colors) {
        readyToRender.value = true;
      } else {
        readyToRender.value = false;
      }
    }
  },
  {
    immediate: true,
  },
);
</script>

<style scoped lang="scss">
.circle {
  background-color: var(--misty);
  border-radius: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-family: var(--custom-font-ltr);
  text-align: center;
  padding: 44px 49px 44px 33px;
  position: relative;
  margin-top: 20px;
  margin-bottom: 30px;
  width: 270px;
  height: 273px;

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    margin-top: 40px;
    margin-bottom: 40px;
    width: 365.1px;
    height: 369.5px;
  }

  &::before {
    left: -13px;
    position: absolute;
    content: '';
    display: block;
    background-color: transparent;
    width: inherit;
    height: inherit;
    border-radius: 100%;
    border: 1px solid var(--primary);

    @media #{map-get($display-breakpoints, 'md-and-up')} {
      left: -25px;
    }
  }

  &__img {
    width: 159px;
    height: 78px;
  }

  &__number {
    color: var(--primary);
    font-size: 75px;
    font-weight: bold;
    line-height: normal;

    &::after {
      background: var(--alpha);
      border-radius: 100%;
      content: '';
      display: flex;
      height: 10px;
      position: relative;
      top: -20px;
      width: 100%;
    }
  }

  &__head {
    font-size: 1.25rem;
    font-weight: bold;
    margin-bottom: 11px;

    @media #{map-get($display-breakpoints, 'md-and-up')} {
      font-size: 1.875rem;
    }
  }

  &__desc {
    font-size: 0.875rem;
    line-height: 1.4;
    width: 183px;

    @media #{map-get($display-breakpoints, 'md-and-up')} {
      width: 307px;
    }
  }
}

.parental-text {
  text-align: center;
  margin-bottom: 15px;
}

.v-locale--is-rtl .circle {
  font-family: var(--custom-font-rtl) !important;
}
</style>
